import React, { useRef, useEffect, useState, useMemo } from 'react';
import { motion, AnimatePresence, useInView } from 'framer-motion';

const TimelineScroller = ({ events, title, description, startYear = 2023, hintYear = 2023 }) => {
  const containerRef = useRef(null);
  const timelineContainerRef = useRef(null);
  const revealRef = useRef(null);

  // Resolve startYear/hintYear to indices — falls back to 0 if year not found
  const startIndex = Math.max(0, events.findIndex(e => e.year === startYear));
  const hintIndex = Math.max(0, events.findIndex(e => e.year === hintYear));

  const [activeIndex, setActiveIndex] = useState(startIndex);
  const [showScrollHint, setShowScrollHint] = useState(true);
  const hasInteracted = useRef(false); // Once user leaves the hint year, hand icon never returns
  const [containerCenter, setContainerCenter] = useState(0);
  const [handIconSrc, setHandIconSrc] = useState(null);
  const [mouseIconSrc, setMouseIconSrc] = useState(null);
  const [handFingerSrc, setHandFingerSrc] = useState(null);
  const [handArrowSrc, setHandArrowSrc] = useState(null);
  const [handArrowLongSrc, setHandArrowLongSrc] = useState(null);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);

  // Track mobile breakpoint on resize
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Scroll-triggered reveal — fires once when 20% of the section is visible
  const isInView = useInView(revealRef, { once: true, amount: 0.2 });

  // Toggle scroll-triggered reveal animation — set to false to turn off
  const showRevealAnimation = true;

  // Toggle edge gradient fade — set to false to turn off
  const showEdgeFade = true;

  // Toggle active bubble pulse — set to false to turn off
  const showBubblePulse = false;

  // Toggle navigation arrows — set to false to hide left/right arrow buttons
  const showNavigationArrows = false;

  // Toggle haptic feedback on timeline snap (Android only, no-op on iOS) — set to false to turn off
  const showHapticFeedback = true;

  // Toggle mobile swipe animation (hand rotation + arrows) — set to false to use original pulse on mobile
  const showMobileSwipeHint = false;

  // Lazy load hint icon — mobile (<768px) uses hand.svg, desktop (≥768px) uses mouse.svg
  useEffect(() => {
    if (activeIndex === hintIndex && showScrollHint) {
      if (isMobile && showMobileSwipeHint) {
        if (!handFingerSrc) {
          import('../images/hand-finger.svg').then((m) => setHandFingerSrc(m.default));
        }
        if (!handArrowSrc) {
          import('../images/hand-arrow.svg').then((m) => setHandArrowSrc(m.default));
        }
        if (!handArrowLongSrc) {
          import('../images/hand-arrow-long.svg').then((m) => setHandArrowLongSrc(m.default));
        }
      } else if (isMobile && !handIconSrc) {
        import('../images/hand.svg').then((module) => {
          setHandIconSrc(module.default);
        });
      } else if (!isMobile && !mouseIconSrc) {
        import('../images/mouse.svg').then((module) => {
          setMouseIconSrc(module.default);
        });
      }
    }
  }, [activeIndex, showScrollHint, handIconSrc, mouseIconSrc, handFingerSrc, handArrowSrc, handArrowLongSrc, hintIndex, isMobile, showMobileSwipeHint]);

  // Haptic feedback on timeline snap — subtle 15ms vibration (Android only, silent no-op on iOS)
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) { isFirstRender.current = false; return; }
    if (showHapticFeedback && navigator.vibrate) {
      navigator.vibrate(15);
    }
  }, [activeIndex, showHapticFeedback]);

  // Responsive timeline dimensions — two-stage fluid scaling
  // Stage 1: 1240px → 768px (desktop to tablet)
  // Stage 2: 768px → 350px (tablet to small mobile)
  const jsClamp = (mobile, tablet, desktop) => {
    const vw = window.innerWidth;
    if (vw >= 1240) return desktop;
    if (vw >= 768) {
      // Scale between tablet and desktop
      const t = (vw - 768) / (1240 - 768);
      return tablet + t * (desktop - tablet);
    }
    if (vw <= 350) return mobile;
    // Scale between mobile and tablet
    const t = (vw - 350) / (768 - 350);
    return mobile + t * (tablet - mobile);
  };

  const [dims, setDims] = useState({
    bubbleActive: 306,
    bubbleInactive: 143,
    gap: 53,
    yearActive: 96,
    yearInactive: 40,
    containerHeight: 311,
    handSize: 48,
    mouseSize: 66,
    handBottom: 50,
    arrowWidth: 32,
    arrowHeight: 14,
  });

  // Recalculate dimensions and container center on mount + resize (debounced)
  useEffect(() => {
    const update = () => {
      setDims({
        //              350px   768px   1240px
        bubbleActive:   jsClamp(150,    214,    306),
        bubbleInactive: jsClamp(70,     100,    143),
        gap:            jsClamp(25,     37,     53),
        yearActive:     jsClamp(46,     67,     96),
        yearInactive:   jsClamp(20,     28,     40),
        containerHeight:jsClamp(160,    218,    311),
        handSize:       jsClamp(24,     34,     48),
        mouseSize:      jsClamp(32,     46,     66),
        handBottom:     jsClamp(25,     36,     50),
        arrowWidth:     jsClamp(16,     24,     32),
        arrowHeight:    jsClamp(7,      10,     14),
      });

      // Calculate container center
      if (timelineContainerRef.current) {
        const rect = timelineContainerRef.current.getBoundingClientRect();
        setContainerCenter(rect.width / 2);
      }
    };

    // Run immediately on mount
    update();

    // Debounced resize — waits 150ms after last resize event before recalculating
    let resizeTimer;
    const handleResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(update, 150);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(resizeTimer);
    };
  }, []);

  // Show/hide scroll hint based on activeIndex — only on first load, never reappears after user navigates away
  useEffect(() => {
    if (activeIndex !== hintIndex) {
      hasInteracted.current = true;
      setShowScrollHint(false);
    }
  }, [activeIndex, hintIndex]);

  // Navigation handlers
  const goToPrevious = () => {
    if (activeIndex > 0) {
      setActiveIndex(prev => prev - 1);
    }
  };

  const goToNext = () => {
    if (activeIndex < events.length - 1) {
      setActiveIndex(prev => prev + 1);
    }
  };

  // Touch handling
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const minSwipeDistance = 50;

  // Prevent horizontal page scroll while swiping inside the timeline.
  // React's onTouchMove is passive by default, so we attach a native
  // non-passive listener that can call preventDefault().
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const handleTouchMove = (e) => {
      if (!touchStart) return;
      const currentX = e.touches[0].clientX;
      const dx = Math.abs(currentX - touchStart);
      const dy = Math.abs(e.touches[0].clientY - (el._touchStartY || 0));
      // If swiping more horizontally than vertically, block page scroll
      if (dx > dy && dx > 10) {
        e.preventDefault();
      }
    };

    const handleTouchStart = (e) => {
      el._touchStartY = e.touches[0].clientY;
    };

    el.addEventListener('touchstart', handleTouchStart, { passive: true });
    el.addEventListener('touchmove', handleTouchMove, { passive: false });
    return () => {
      el.removeEventListener('touchstart', handleTouchStart);
      el.removeEventListener('touchmove', handleTouchMove);
    };
  }, [touchStart]);

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe && activeIndex < events.length - 1) {
      setActiveIndex(prev => prev + 1);
    }
    if (isRightSwipe && activeIndex > 0) {
      setActiveIndex(prev => prev - 1);
    }
  };

  // Mouse click-and-drag handling (desktop) — live dragging, snaps to nearest on release
  const [mouseStart, setMouseStart] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);
  const justDraggedRef = useRef(false); // Prevents bubble onClick from firing after a drag

  const onMouseDown = (e) => {
    // Ignore clicks on buttons and interactive elements (arrows, dots, bubbles)
    if (e.target.closest('button') || e.target.closest('[data-no-drag]')) return;
    setMouseStart(e.clientX);
    setIsDragging(false); // Don't set dragging until mouse actually moves
    setDragOffset(0);
  };

  // Tracks which bubble would become active if the user releases the drag now
  const [dragPreviewIndex, setDragPreviewIndex] = useState(null);

  const onMouseMove = (e) => {
    if (mouseStart === null) return;
    const moved = Math.abs(e.clientX - mouseStart);
    // Only activate drag after 8px of movement — prevents interfering with clicks
    if (!isDragging && moved < 8) return;
    if (!isDragging) setIsDragging(true);
    e.preventDefault();
    // Live offset: positive = dragging right (go to earlier), negative = dragging left (go to later)
    let currentDrag = e.clientX - mouseStart;

    // Clamp drag at boundaries — lock direction when at first/last item
    // At first item: block dragging right (positive offset = going earlier, nothing there)
    if (activeIndex === 0 && currentDrag > 0) currentDrag = 0;
    // At last item: block dragging left (negative offset = going later, nothing there)
    if (activeIndex === events.length - 1 && currentDrag < 0) currentDrag = 0;

    setDragOffset(currentDrag);

    // Calculate which bubble would snap into place if released now
    const stepSize = dims.bubbleInactive + dims.gap;
    const steps = Math.round(Math.abs(currentDrag) / stepSize);
    let previewIdx;
    if (steps === 0) {
      previewIdx = activeIndex;
    } else if (currentDrag < 0) {
      previewIdx = Math.min(activeIndex + steps, events.length - 1);
    } else {
      previewIdx = Math.max(activeIndex - steps, 0);
    }
    setDragPreviewIndex(previewIdx);
  };

  const onMouseUp = (e) => {
    if (mouseStart === null) return;
    // If we never entered drag mode, it was just a click — let it through
    if (!isDragging) { setMouseStart(null); return; }
    const totalDrag = e.clientX - mouseStart;
    setIsDragging(false);
    setMouseStart(null);
    setDragOffset(0);
    setDragPreviewIndex(null);

    // Flag that a drag just completed — prevents bubble onClick from overriding
    justDraggedRef.current = true;
    requestAnimationFrame(() => { justDraggedRef.current = false; });

    // Calculate how many events to skip based on drag distance
    // Each event step is roughly bubbleInactive + gap pixels
    const stepSize = dims.bubbleInactive + dims.gap;
    const steps = Math.round(Math.abs(totalDrag) / stepSize);

    if (steps === 0) return; // Too small, treat as a click (bubble onClick handles it)

    if (totalDrag < 0) {
      // Dragged left → go forward
      setActiveIndex(prev => Math.min(prev + steps, events.length - 1));
    } else {
      // Dragged right → go backward
      setActiveIndex(prev => Math.max(prev - steps, 0));
    }
  };

  const onMouseLeave = () => {
    if (isDragging) {
      setIsDragging(false);
      setMouseStart(null);
      setDragOffset(0);
      setDragPreviewIndex(null);
    }
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft' && activeIndex > 0) {
        setActiveIndex(prev => prev - 1);
      } else if (e.key === 'ArrowRight' && activeIndex < events.length - 1) {
        setActiveIndex(prev => prev + 1);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeIndex, events.length]);

  // Memoize the centering offset — includes live dragOffset during mouse drag
  const bubbleLeftPosition = useMemo(() => {
    let offset = dims.bubbleActive / 2;
    for (let i = 0; i < activeIndex; i++) {
      offset += dims.bubbleInactive + dims.gap;
    }
    const base = containerCenter > 0
      ? containerCenter - offset
      : 0;
    return `${base + dragOffset}px`;
  }, [activeIndex, dims.bubbleActive, dims.bubbleInactive, dims.gap, containerCenter, dragOffset]);

  return (
    <div 
      ref={(el) => { containerRef.current = el; revealRef.current = el; }}
      className={`relative w-full py-16 md:py-24${isDragging ? ' select-none' : ''}`}
      style={{ touchAction: 'pan-y' }}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      onMouseDown={onMouseDown}
      onMouseMove={onMouseMove}
      onMouseUp={onMouseUp}
      onMouseLeave={onMouseLeave}
    >
      {/* Header */}
      <motion.div
        initial={showRevealAnimation ? { opacity: 0, y: 30 } : false}
        animate={showRevealAnimation ? (isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }) : false}
        transition={{ duration: 0.6, ease: "easeOut", delay: 0 }}
        className="md:mx-[1.5rem] x1270:mx-[4.5rem] mb-12 md:w-full xs:w-4/5 xs:mx-auto"
      >
        <h2 className="module-headlineL !text-white ">
          {title}
        </h2>
        <p className="module-bodycopyL !text-white w-full md:max-w-[65%]">
          {description}
        </p>
      </motion.div>

      {/* Timeline and Navigation Wrapper */}
      <motion.div
        initial={showRevealAnimation ? { opacity: 0, y: 30 } : false}
        animate={showRevealAnimation ? (isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }) : false}
        transition={{ duration: 0.6, ease: "easeOut", delay: 0.2 }}
        className="relative"
      >
        {/* Progress Dots — larger on mobile (≤768px) for easier tapping */}
        <motion.div
          data-no-drag
          initial={showRevealAnimation ? { opacity: 0, y: 20 } : false}
          animate={showRevealAnimation ? (isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }) : false}
          transition={{ duration: 0.5, ease: "easeOut", delay: 0.5 }}
          className="flex justify-center gap-2 mb-6"
        >
          {events.map((_, index) => {
            const isMobileView = window.innerWidth <= 768;
            const dotHeight = isMobileView ? 12 : 8;
            const dotWidthActive = isMobileView ? 42 : 32;
            const dotWidthInactive = isMobileView ? 12 : 8;

            return (
              <motion.button
                key={index}
                onClick={() => setActiveIndex(index)}
                animate={{
                  backgroundColor: activeIndex === index ? '#DCB1FA' : '#FABBAA',
                  width: activeIndex === index ? `${dotWidthActive}px` : `${dotWidthInactive}px`,
                  height: `${dotHeight}px`
                }}
                transition={{ duration: 0.3 }}
                className="rounded-full cursor-pointer"
                aria-label={`Go to event ${index + 1}`}
              />
            );
          })}
        </motion.div>

        {/* Navigation Arrow - Left side (hidden on mobile, users swipe or tap) */}
        {showNavigationArrows && (
        <AnimatePresence>
        {activeIndex > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="hidden md:flex absolute top-1/2 -translate-y-1/2 left-[1%] z-20"
          >
            <button
              onClick={goToPrevious}
              className="w-8 h-8 rounded-full flex items-center justify-center bg-[#b24029] hover:bg-opacity-80 transition-opacity"
              aria-label="Previous event"
            >
              <svg width="12" height="20" viewBox="0 0 12 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M10 18L2 10L10 2" stroke="#ffe1d9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </motion.div>
        )}
        </AnimatePresence>
        )}

        {/* Navigation Arrow - Right side (hidden on mobile, users swipe or tap) */}
        {showNavigationArrows && (
        <AnimatePresence>
        {activeIndex < events.length - 1 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="hidden md:flex absolute top-1/2 -translate-y-1/2 right-[1%] z-20"
          >
            <button
              onClick={goToNext}
              className="w-8 h-8 rounded-full flex items-center justify-center bg-[#b24029] hover:bg-opacity-80 transition-opacity"
              aria-label="Next event"
            >
              <svg width="12" height="20" viewBox="0 0 12 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M2 18L10 10L2 2" stroke="#ffe1d9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </motion.div>
        )}
        </AnimatePresence>
        )}

        {/* Timeline Container - Responsive height */}
        <div 
          ref={timelineContainerRef}
          id="timeline-container" 
          className="relative overflow-hidden mb-4 md:mx-[5%]"
          style={{ height: `${dims.containerHeight}px`, cursor: isDragging ? 'grabbing' : 'grab' }}
        >
        {/* Dotted line extending to left - only shows on first item */}
        <AnimatePresence>
        {activeIndex === 0 && (
          <div
            className="absolute xs:left-[8%] md:left-0 z-0"
            style={{ 
              top: '50%',
              transform: 'translateY(-50%)',
              width: `calc(50% - ${dims.bubbleActive / 2}px)`,
              height: '32px'
            }}
          >
            <motion.svg
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -30 }}
              transition={{ duration: 0.5, ease: "easeOut" }}
              className="w-full h-full"
            >
              <line 
                x1="0" 
                y1="16" 
                x2="100%" 
                y2="16" 
                stroke="#ffe1d9" 
                strokeWidth="2" 
                strokeDasharray="4 4"
              />
              {/* White circle at the start of the line */}
              <circle cx="16" cy="16" r="16" fill="#ffffff" />
            </motion.svg>
          </div>
        )}
        </AnimatePresence>

        {/* Dotted line extending to right - only shows on last item */}
        <AnimatePresence>
        {activeIndex === events.length - 1 && (
          <div
            className="absolute xs:right-[8%] md:right-0 z-0"
            style={{ 
              top: '50%',
              transform: 'translateY(-50%)',
              width: `calc(50% - ${dims.bubbleActive / 2}px)`,
              height: '32px'
            }}
          >
            <motion.svg
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 30 }}
              transition={{ duration: 0.5, ease: "easeOut" }}
              className="w-full h-full"
            >
              <line 
                x1="0" 
                y1="16" 
                x2="100%" 
                y2="16" 
                stroke="#ffe1d9" 
                strokeWidth="2" 
                strokeDasharray="4 4"
              />
              {/* White circle at the end of the line */}
              <circle cx="calc(100% - 16px)" cy="16" r="16" fill="#ffffff" />
            </motion.svg>
          </div>
        )}
        </AnimatePresence>

        {/* Timeline Bubbles Container - Centers active bubble */}
        <motion.div 
          className="relative flex items-center"
          style={{
            position: 'absolute',
            top: '50%',
            transform: 'translateY(-50%)',
            gap: `${dims.gap}px`
          }}
          animate={{
            left: bubbleLeftPosition
          }}
          transition={isDragging
            ? { duration: 0 }
            : { duration: 0.5, ease: [0.4, 0.0, 0.2, 1] }
          }
        >
          {events.map((event, index) => {
            const isActive = index === activeIndex;
            const isDragPreview = isDragging && dragPreviewIndex === index && !isActive;
            // Preview bubble grows to 1.15x its inactive size
            const previewSize = Math.round(dims.bubbleInactive * 1.15);
            const bubbleSize = isActive ? dims.bubbleActive : isDragPreview ? previewSize : dims.bubbleInactive;

            return (
              <div 
                key={index} 
                className="relative flex flex-col items-center shrink-0"
              >
                {/* Pulse wrapper — subtle breathing animation on active bubble only */}
                <motion.div
                  animate={
                    isActive && showBubblePulse
                      ? { scale: [1, 1.02, 1] }
                      : { scale: 1 }
                  }
                  transition={
                    isActive && showBubblePulse
                      ? { duration: 2.5, repeat: Infinity, ease: "easeInOut", delay: 0.5 }
                      : { duration: 0.3 }
                  }
                >
                {/* Year Bubble */}
                <motion.div
                  onClick={() => { if (!justDraggedRef.current) setActiveIndex(index); }}
                  animate={{
                    width: `${bubbleSize}px`,
                    height: `${bubbleSize}px`,
                    backgroundColor: isActive ? '#DCB1FA' : isDragPreview ? '#d9a6f5' : '#FABBAA',
                  }}
                  transition={{
                    duration: isDragPreview ? 0.25 : 0.5,
                    ease: [0.4, 0.0, 0.2, 1]
                  }}
                  className="rounded-full flex items-center justify-center cursor-pointer relative z-10 select-none"
                >
                  <motion.span 
                    className="font-sans antialiased text-[#b24029]" 
                    animate={{
                      fontSize: isActive ? `${dims.yearActive}px` : `${dims.yearInactive}px`
                    }}
                    transition={{
                      duration: 0.5,
                      ease: [0.4, 0.0, 0.2, 1]
                    }}
                    style={{ fontWeight: 400 }}
                  >
                    {event.year}
                  </motion.span>

                  {/* Scroll Hint Icon - on hint bubble only */}
                  {/* Mobile (<768px): hand.svg / Desktop (≥768px): mouse.svg */}
                  {isActive && index === hintIndex && showScrollHint && (!isMobile || !showMobileSwipeHint) && (isMobile ? handIconSrc : mouseIconSrc) && (
                    <motion.div 
                      className="absolute pointer-events-none z-20"
                      style={{
                        bottom: `${dims.handBottom}px`,
                        left: isMobile ? '42%' : '38%',
                        transform: 'translateX(-50%)',
                        width: `${isMobile ? dims.handSize : dims.mouseSize}px`,
                        height: `${isMobile ? dims.handSize : dims.mouseSize}px`,
                      }}
                      animate={{ scale: [1, 0.8, 1] }}
                      transition={{ duration: 0.8, repeat: Infinity, ease: "easeInOut" }}
                    >
                      <img src={isMobile ? handIconSrc : mouseIconSrc} alt="Scroll hint" className="w-full h-full" />
                    </motion.div>
                  )}

                  {/* Mobile: composite hand-finger + arrows with rotation/extend animation */}
                  {isActive && index === hintIndex && showScrollHint && isMobile && showMobileSwipeHint && handFingerSrc && handArrowSrc && handArrowLongSrc && (() => {
                    // Hand-finger SVG native aspect ratio: 119×160
                    const fingerHeight = Math.round(dims.handSize * (160 / 119));
                    const arrowY = Math.round(fingerHeight * 0.05) - 2;
                    const totalWidth = dims.arrowWidth + dims.handSize + dims.arrowWidth;

                    return (
                    <motion.div
                      className="absolute pointer-events-none z-20"
                      style={{
                        bottom: `${dims.handBottom - 8}px`,
                        left: '42%',
                        transform: 'translateX(-50%)',
                        width: `${totalWidth}px`,
                        height: `${fingerHeight}px`,
                      }}
                    >
                      {/* Left arrow — uses longer SVG for more visible shaft */}
                      <motion.div
                        style={{
                          position: 'absolute',
                          top: `${arrowY}px`,
                          right: `calc(40% + ${dims.handSize * 0.15 - 16}px)`,
                          width: `${Math.round(dims.arrowHeight * (148 / 41))}px`,
                          height: `${dims.arrowHeight}px`,
                          overflow: 'hidden',
                        }}
                        animate={{ clipPath: ['inset(0 0% 0 0)', 'inset(0 35% 0 0)', 'inset(0 0% 0 0)'] }}
                        transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
                      >
                        <img src={handArrowLongSrc} alt="" style={{ width: '100%', height: '100%' }} />
                      </motion.div>

                      {/* Right arrow — mirrored, clipPath reveals/hides shaft from the right */}
                      <motion.div
                        style={{
                          position: 'absolute',
                          top: `${arrowY}px`,
                          left: `calc(50% + ${dims.handSize * 0.15 + 16}px)`,
                          width: `${dims.arrowWidth}px`,
                          height: `${dims.arrowHeight}px`,
                          overflow: 'hidden',
                          transform: 'scaleX(-1)',
                        }}
                        animate={{ clipPath: ['inset(0 30% 0 0)', 'inset(0 0% 0 0)', 'inset(0 30% 0 0)'] }}
                        transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
                      >
                        <img src={handArrowSrc} alt="" style={{ width: '100%', height: '100%' }} />
                      </motion.div>

                      {/* Hand/finger — rotates from the wrist (bottom-center) */}
                      <motion.img
                        src={handFingerSrc}
                        alt="Scroll hint"
                        style={{
                          position: 'absolute',
                          bottom: '0',
                          left: '50%',
                          width: `${dims.handSize}px`,
                          height: `${fingerHeight}px`,
                          transform: 'translateX(-50%)',
                          zIndex: 1,
                          transformOrigin: 'center 75%',
                        }}
                        animate={{ rotate: [10, -10, 10] }}
                        transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
                      />
                    </motion.div>
                    );
                  })()}
                </motion.div>
                </motion.div>

                {/* Connecting Line to next bubble */}
                {index < events.length - 1 && (() => {
                  // Line starts at the right edge of this bubble and spans the flex gap.
                  // bubbleSize already accounts for active / preview / inactive width,
                  // and the flex gap (dims.gap) stays constant — no need to shrink it.
                  return (
                  <motion.svg
                    className="absolute"
                    style={{ 
                      top: '50%',
                      transform: 'translateY(-50%)',
                      width: `${dims.gap}px`,
                      height: '2px',
                      zIndex: 0
                    }}
                    animate={{
                      left: `${bubbleSize}px`
                    }}
                    transition={{
                      duration: isDragPreview ? 0.25 : 0.5,
                      ease: [0.4, 0.0, 0.2, 1]
                    }}
                  >
                    <line
                      x1="0"
                      y1="1"
                      x2={dims.gap}
                      y2="1"
                      stroke="#ffffff"
                      strokeWidth="2"
                    />
                  </motion.svg>
                  );
                })()}
              </div>
            );
          })}
        </motion.div>

        {/* Edge gradient fades — hint that more content exists off-screen */}
        {showEdgeFade && (
          <>
            <div
              className="absolute left-0 top-0 h-full pointer-events-none z-10"
              style={{
                width: '60px',
                background: 'linear-gradient(to right, #ff794e, transparent)',
              }}
            />
            <div
              className="absolute right-0 top-0 h-full pointer-events-none z-10"
              style={{
                width: '60px',
                background: 'linear-gradient(to left, #ff794e, transparent)',
              }}
            />
          </>
        )}
      </div>
      </motion.div>

      {/* Description Area - Centered below */}
      <motion.div
        initial={showRevealAnimation ? { opacity: 0, y: 30 } : false}
        animate={showRevealAnimation ? (isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }) : false}
        transition={{ duration: 0.6, ease: "easeOut", delay: 0.4 }}
        className="relative min-h-[180px] px-4 md:px-[140px] mt-4"
      >
        {/* Brown vertical line - always visible */}
        <div className="flex justify-center">
          <div className="w-[2px] h-[22px] bg-[#b24029]" />
        </div>

        <AnimatePresence mode="wait">
          {events.map((event, index) => {
            if (activeIndex !== index) return null;
            
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3, ease: "easeInOut" }}
                className="flex flex-col items-center"
              >
                {/* Description text container - 72% width, centered */}
                <div className="w-full  xs:max-w-[80%] md:max-w-[36%] mt-1 mx-auto">
                  <p className="module-bodycopy-timeline text-center">
                    {event.description}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </motion.div>

    </div>
  );
};

export default TimelineScroller;
